import React from 'react';

// Driving License Result Component
export const DrivingLicenseResult: React.FC<{ data: any }> = ({ data }) => {
  const licenseData = data?.data?.data || data?.data || {};
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-green-50 px-6 py-4 border-b border-green-200">
        <h3 className="text-lg font-semibold text-green-900 flex items-center">
          🚗 Driving License Verification Result
          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
            Verified
          </span>
        </h3>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Personal Information</h4>
            <table className="w-full text-sm">
              <tbody className="space-y-2">
                <tr>
                  <td className="font-medium text-gray-700 py-1">License Number:</td>
                  <td className="text-gray-900 py-1">{licenseData.license_number || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Name:</td>
                  <td className="text-gray-900 py-1">{licenseData.name || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Father/Husband:</td>
                  <td className="text-gray-900 py-1">{licenseData.father_or_husband_name || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Gender:</td>
                  <td className="text-gray-900 py-1">{licenseData.gender || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Date of Birth:</td>
                  <td className="text-gray-900 py-1">{licenseData.dob || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Blood Group:</td>
                  <td className="text-gray-900 py-1">{licenseData.blood_group || 'Not specified'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* License Details */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">License Details</h4>
            <table className="w-full text-sm">
              <tbody className="space-y-2">
                <tr>
                  <td className="font-medium text-gray-700 py-1">State:</td>
                  <td className="text-gray-900 py-1">{licenseData.state || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">RTO Office:</td>
                  <td className="text-gray-900 py-1">{licenseData.ola_name || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Date of Issue:</td>
                  <td className="text-gray-900 py-1">{licenseData.doi || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Date of Expiry:</td>
                  <td className="text-gray-900 py-1">{licenseData.doe || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Vehicle Classes:</td>
                  <td className="text-gray-900 py-1">
                    {licenseData.vehicle_classes?.join(', ') || 'N/A'}
                  </td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Profile Image:</td>
                  <td className="text-gray-900 py-1">
                    {licenseData.has_image ? '✅ Available' : '❌ Not available'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Address Information */}
        <div className="mt-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Address Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-medium text-gray-700 mb-1">Permanent Address:</p>
              <p className="text-gray-900 text-sm bg-gray-50 p-3 rounded">
                {licenseData.permanent_address || 'N/A'}
                {licenseData.permanent_zip && ` - ${licenseData.permanent_zip}`}
              </p>
            </div>
            <div>
              <p className="font-medium text-gray-700 mb-1">Temporary Address:</p>
              <p className="text-gray-900 text-sm bg-gray-50 p-3 rounded">
                {licenseData.temporary_address || 'N/A'}
                {licenseData.temporary_zip && ` - ${licenseData.temporary_zip}`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// PAN Verification Result Component
export const PANVerificationResult: React.FC<{ data: any }> = ({ data }) => {
  const panData = data?.data?.response || {};
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 flex items-center">
          🆔 PAN Card Verification Result
          <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
            {panData.isValid ? 'Valid' : 'Invalid'}
          </span>
        </h3>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Personal Details</h4>
            <table className="w-full text-sm">
              <tbody className="space-y-2">
                <tr>
                  <td className="font-medium text-gray-700 py-1">PAN Number:</td>
                  <td className="text-gray-900 py-1 font-mono">{panData.pan || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Full Name:</td>
                  <td className="text-gray-900 py-1">{panData.name || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">First Name:</td>
                  <td className="text-gray-900 py-1">{panData.firstName || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Middle Name:</td>
                  <td className="text-gray-900 py-1">{panData.middleName || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Last Name:</td>
                  <td className="text-gray-900 py-1">{panData.lastName || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Gender:</td>
                  <td className="text-gray-900 py-1">{panData.gender === 'M' ? 'Male' : panData.gender === 'F' ? 'Female' : panData.gender || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Date of Birth:</td>
                  <td className="text-gray-900 py-1">{panData.dob || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Status & Verification */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Verification Status</h4>
            <table className="w-full text-sm">
              <tbody className="space-y-2">
                <tr>
                  <td className="font-medium text-gray-700 py-1">Holder Type:</td>
                  <td className="text-gray-900 py-1">{panData.typeOfHolder || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Valid Status:</td>
                  <td className="text-gray-900 py-1">
                    {panData.isValid ? '✅ Valid' : '❌ Invalid'}
                  </td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Aadhaar Seeding:</td>
                  <td className="text-gray-900 py-1">
                    {panData.aadhaarSeedingStatus ? '✅ Linked' : '❌ Not linked'}
                  </td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Tax Status:</td>
                  <td className="text-gray-900 py-1">
                    {panData.tax ? '✅ Active' : '❌ Inactive'}
                  </td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Masked Aadhaar:</td>
                  <td className="text-gray-900 py-1 font-mono">{panData.maskedAadhaar || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Last 4 Digits:</td>
                  <td className="text-gray-900 py-1 font-mono">{panData.lastFourDigit || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Address & Contact */}
        <div className="mt-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Address & Contact Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-medium text-gray-700 mb-1">Address:</p>
              <p className="text-gray-900 text-sm bg-gray-50 p-3 rounded">
                {panData.address || 'N/A'}
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <p className="font-medium text-gray-700">City: <span className="font-normal">{panData.city || 'N/A'}</span></p>
              </div>
              <div>
                <p className="font-medium text-gray-700">State: <span className="font-normal">{panData.state || 'N/A'}</span></p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Country: <span className="font-normal">{panData.country || 'N/A'}</span></p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Pincode: <span className="font-normal">{panData.pincode || 'N/A'}</span></p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Mobile: <span className="font-normal">{panData.mobile_no || 'N/A'}</span></p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Email: <span className="font-normal">{panData.email || 'N/A'}</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Voter ID Result Component
export const VoterIDResult: React.FC<{ data: any }> = ({ data }) => {
  const voterData = data?.data?.data || data?.data || {};
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-purple-50 px-6 py-4 border-b border-purple-200">
        <h3 className="text-lg font-semibold text-purple-900 flex items-center">
          🗳️ Voter ID Verification Result
          <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
            Verified
          </span>
        </h3>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Personal Information</h4>
            <table className="w-full text-sm">
              <tbody className="space-y-2">
                <tr>
                  <td className="font-medium text-gray-700 py-1">EPIC Number:</td>
                  <td className="text-gray-900 py-1 font-mono">{voterData.epic_no || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Name:</td>
                  <td className="text-gray-900 py-1">{voterData.name || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Gender:</td>
                  <td className="text-gray-900 py-1">{voterData.gender === 'M' ? 'Male' : voterData.gender === 'F' ? 'Female' : voterData.gender || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Age:</td>
                  <td className="text-gray-900 py-1">{voterData.age || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Relation Name:</td>
                  <td className="text-gray-900 py-1">{voterData.relation_name || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Relation Type:</td>
                  <td className="text-gray-900 py-1">{voterData.relation_type === 'FTHR' ? 'Father' : voterData.relation_type || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Location Information */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Location Details</h4>
            <table className="w-full text-sm">
              <tbody className="space-y-2">
                <tr>
                  <td className="font-medium text-gray-700 py-1">State:</td>
                  <td className="text-gray-900 py-1">{voterData.state || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">District:</td>
                  <td className="text-gray-900 py-1">{voterData.district || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Area:</td>
                  <td className="text-gray-900 py-1">{voterData.area || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">House Number:</td>
                  <td className="text-gray-900 py-1">{voterData.house_no || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Electoral Information */}
        <div className="mt-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Electoral Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <table className="w-full text-sm">
                <tbody className="space-y-2">
                  <tr>
                    <td className="font-medium text-gray-700 py-1">Assembly Constituency:</td>
                    <td className="text-gray-900 py-1">{voterData.assembly_constituency || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="font-medium text-gray-700 py-1">Constituency Number:</td>
                    <td className="text-gray-900 py-1">{voterData.assembly_constituency_number || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="font-medium text-gray-700 py-1">Parliamentary Constituency:</td>
                    <td className="text-gray-900 py-1">{voterData.parliamentary_constituency || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="font-medium text-gray-700 py-1">Parliamentary Number:</td>
                    <td className="text-gray-900 py-1">{voterData.parliamentary_number || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <table className="w-full text-sm">
                <tbody className="space-y-2">
                  <tr>
                    <td className="font-medium text-gray-700 py-1">Polling Station:</td>
                    <td className="text-gray-900 py-1">{voterData.polling_station || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="font-medium text-gray-700 py-1">Part Number:</td>
                    <td className="text-gray-900 py-1">{voterData.part_number || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="font-medium text-gray-700 py-1">Part Name:</td>
                    <td className="text-gray-900 py-1">{voterData.part_name || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="font-medium text-gray-700 py-1">Serial Number in Part:</td>
                    <td className="text-gray-900 py-1">{voterData.slno_inpart || 'N/A'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// FSSAI Result Component
export const FSSAIResult: React.FC<{ data: any }> = ({ data }) => {
  const fssaiData = data?.data?.data || data?.data || {};
  const details = fssaiData.details?.[0] || {};
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-orange-50 px-6 py-4 border-b border-orange-200">
        <h3 className="text-lg font-semibold text-orange-900 flex items-center">
          🍽️ FSSAI License Verification Result
          <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
            {details.status_desc || 'Verified'}
          </span>
        </h3>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* License Information */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">License Details</h4>
            <table className="w-full text-sm">
              <tbody className="space-y-2">
                <tr>
                  <td className="font-medium text-gray-700 py-1">FSSAI Number:</td>
                  <td className="text-gray-900 py-1 font-mono">{fssaiData.fssai_number || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Application Number:</td>
                  <td className="text-gray-900 py-1 font-mono">{fssaiData.application_number || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Business Name:</td>
                  <td className="text-gray-900 py-1">{details.company_name || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">License Category:</td>
                  <td className="text-gray-900 py-1">{details.license_category_name || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Application Type:</td>
                  <td className="text-gray-900 py-1">{details.app_type_desc || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Status:</td>
                  <td className="text-gray-900 py-1">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      details.status_desc === 'License Issued' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {details.status_desc || 'N/A'}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Timeline Information */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Timeline & Reference</h4>
            <table className="w-full text-sm">
              <tbody className="space-y-2">
                <tr>
                  <td className="font-medium text-gray-700 py-1">Submission Date:</td>
                  <td className="text-gray-900 py-1">{details.app_submission_date || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Last Updated:</td>
                  <td className="text-gray-900 py-1">{details.last_updated_on || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Reference ID:</td>
                  <td className="text-gray-900 py-1 font-mono">{details.ref_id || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">Display Ref ID:</td>
                  <td className="text-gray-900 py-1 font-mono">{details.display_ref_id || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="font-medium text-gray-700 py-1">FBO ID:</td>
                  <td className="text-gray-900 py-1 font-mono">{details.fbo_id || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Address Information */}
        <div className="mt-6">
          <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Business Address</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-medium text-gray-700 mb-1">Premises Address:</p>
              <p className="text-gray-900 text-sm bg-gray-50 p-3 rounded">
                {details.address_premises || 'N/A'}
              </p>
            </div>
            <div className="space-y-2">
              <div>
                <p className="font-medium text-gray-700">State: <span className="font-normal">{details.state_name || 'N/A'}</span></p>
              </div>
              <div>
                <p className="font-medium text-gray-700">District: <span className="font-normal">{details.district_name || 'N/A'}</span></p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Taluk: <span className="font-normal">{details.taluk_name || 'N/A'}</span></p>
              </div>
              <div>
                <p className="font-medium text-gray-700">Pincode: <span className="font-normal">{details.pincode || 'N/A'}</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Drug License Result Component (placeholder for future implementation)
export const DrugLicenseResult: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="bg-pink-50 px-6 py-4 border-b border-pink-200">
        <h3 className="text-lg font-semibold text-pink-900 flex items-center">
          💊 Drug License Verification Result
        </h3>
      </div>
      
      <div className="p-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Raw API Response:</h4>
          <pre className="text-sm text-gray-700 whitespace-pre-wrap overflow-x-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Drug license result display will be implemented once we receive sample response data.
        </p>
      </div>
    </div>
  );
};
